import { memo, useRef, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, Col, Label, Row } from 'reactstrap'
import useClickOutside from '../Hooks/useClickOutside'
import { DropzoneProps } from './types'

const Dropzone = (props: DropzoneProps) => {
    const { callback, tab, existingFilesLength = 0 } = props
    const cardRef = useRef(null)

    const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([])
    const [uploadError, setUploadError] = useState(false)

    const isImage = tab === 'photos' || tab === 'groundPlans'

    const maxAllowedPhotos = 40
    const maxAllowedGroundplans = 10

    const { t } = useTranslation()

    useClickOutside(cardRef, setUploadError)

    const { getRootProps, getInputProps } = useDropzone({
        multiple: isImage,
        // for image accept specified extensions, for other files accepts anything (could be more specified for certain extensions...)
        accept: isImage ? { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif'] } : undefined,
        onDrop: (acceptedFiles) => {
            const allowUpload = generateUploadCondition(existingFilesLength, acceptedFiles.length)

            if (allowUpload) {
                callback(acceptedFiles)
                setUploadError(false)
            } else {
                setUploadError(true)
                return
            }
        },
        onDropRejected: (rejectedFiles) => {
            setRejectedFiles(rejectedFiles)
        },
    })

    const generateUploadCondition = (existingFilesLength: number, acceptedFilesLength: number): boolean => {
        const conditions: Record<string, boolean> = {
            photos: existingFilesLength + acceptedFilesLength <= maxAllowedPhotos,
            groundPlans: existingFilesLength + acceptedFilesLength <= maxAllowedGroundplans,
            documents: true,
        }
        return conditions[tab] ?? false
    }

    const displayDropzoneMessage = (): string => {
        const messages: Record<string, string> = {
            photos: t('dropzone.images.photos.label'),
            groundPlans: t('dropzone.images.groundPlans.label'),
            documents: t('dropzone.documents.label'),
        }
        return messages[tab] ?? ''
    }

    return (
        <div ref={cardRef}>
            <Card>
                <CardBody>
                    <Row>
                        <Col sm={12} className="mb-4">
                            <Label>{displayDropzoneMessage()}</Label>
                            <div {...getRootProps({ className: 'dropzone dz-clickable' })} data-testid="dropzone">
                                <div className="dz-message needsclick">
                                    <input {...getInputProps()} data-testid="file-input" />
                                    <div className="d-flex flex-column">
                                        <i className="fs-36 mdi mdi-cloud-upload-outline" style={{ lineHeight: 1 }} />
                                        <span className="fs-13">{t('dropzone.message')}</span>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        {rejectedFiles.length ? (
                            <p className="text-danger text-center">{isImage ? t('dropzone.images.error') : t('dropzone.documents.error')}</p>
                        ) : null}

                        {uploadError ? <p className="text-danger text-center">{t('dropzone.upload.error')}</p> : null}
                    </Row>
                </CardBody>
            </Card>
        </div>
    )
}

export default memo(Dropzone)
